// Copy from Lambda Layer

export enum ActivitySource {
  TATTA = 0,
  Garmin = 1,
  Neorun = 2,
  AppleWatch = 3,
  HealthKit = 4,
  GoogleFit = 5,
  Polar = 6,
}

export const getActivitySourceName = (activitySource: ActivitySource, dataOrigin?: string): string => {
  switch (activitySource) {
    case ActivitySource.TATTA:
      return 'TATTA';
    case ActivitySource.Garmin:
      return 'Garmin Connect';
    case ActivitySource.Neorun:
      return 'Neorun / Epson View';
    case ActivitySource.AppleWatch:
      return 'AppleWatch';
    case ActivitySource.HealthKit:
      return dataOrigin ? `HealthKit ${(dataOrigin)}` : `HealthKit`;
    case ActivitySource.GoogleFit:
      return dataOrigin ? `GoogleFit/HC ${(dataOrigin)}` : `GoogleFit/HC`;
    case ActivitySource.Polar:
      return 'Polar';
    default:
      return '';
  }
};

export enum ActivityType {
  RUNNING = 0,
  TRAIL_RUNNING = 1,
  CYCLING = 2,
  WALKING = 3,
  HIKING = 4,
  UNKNOWN = 5
}

export const getActivityTypeName = (activityType: ActivityType | undefined): string => {
  switch (activityType) {
    case ActivityType.RUNNING:
      return 'RUNNING';
    case ActivityType.TRAIL_RUNNING:
      return 'TRAIL_RUNNING';
    case ActivityType.CYCLING:
      return 'CYCLING';
    case ActivityType.WALKING:
      return 'WALKING';
    case ActivityType.HIKING:
      return 'HIKING';
    case ActivityType.UNKNOWN:
      return 'UNKNOWN';
    default:
      return '';
  }
};

export enum CheatType {
  VALID,
  JUDGED_BY_ADMIN,
  SUMMARY_DIST_IS_TOO_LONG,
  SUMMARY_DIST_IS_OVERED_LAP_DIST, // トレッドミルやインドアサイクリング、計測不備の場合の手入力編集に対応できないため、自動判定でのフラグ管理には使用しない
  SUMMARY_PACE_IS_OVERED_WORLD_RECORD,
  LAP_PACE_IS_OVERED_WORLD_RECORD,
  INVALID_MOTION,
  EDITED_OLD_DATA,
  LAP_IS_SKIPPED,
  VERY_STRICT_INVALID_MOTION,
  GROSS_TIME_IS_TOO_LONG,
}

export const getCheatTypeName = (cheatType?: CheatType): string => {
  switch (cheatType) {
    case CheatType.VALID:
      return '';
    case CheatType.JUDGED_BY_ADMIN:
      return 'JUDGED_BY_ADMIN';
    case CheatType.SUMMARY_DIST_IS_TOO_LONG:
      return 'SUMMARY_DIST_IS_TOO_LONG';
    case CheatType.SUMMARY_DIST_IS_OVERED_LAP_DIST:
      return 'SUMMARY_DIST_IS_OVERED_LAP_DIST';
    case CheatType.SUMMARY_PACE_IS_OVERED_WORLD_RECORD:
      return 'SUMMARY_PACE_IS_OVERED_WORLD_RECORD';
    case CheatType.LAP_PACE_IS_OVERED_WORLD_RECORD:
      return 'LAP_PACE_IS_OVERED_WORLD_RECORD';
    case CheatType.INVALID_MOTION:
      return 'INVALID_MOTION';
    case CheatType.EDITED_OLD_DATA:
      return 'EDITED_OLD_DATA';
    case CheatType.LAP_IS_SKIPPED:
      return 'LAP_IS_SKIPPED';
    case CheatType.VERY_STRICT_INVALID_MOTION:
      return 'VERY_STRICT_INVALID_MOTION';
    case CheatType.GROSS_TIME_IS_TOO_LONG:
      return 'GROSS_TIME_IS_TOO_LONG';
    case undefined:
      return '';
  }
  return 'UNKNOWN_CHEAT_TYPE';
};

/* tslint:disable */
export namespace ActivityField {
  export type id = number;
  export type user_id = number | undefined;
  export type uuid = string | undefined;
  export type activity_type = number | undefined;
  export type pace = number;
  export type distance = number;
  export type calorie = number;
  export type climb_up = number;
  export type climb_down = number;
  export type beginning_at = number | undefined;
  export type end_of_at = number | undefined;
  export type time_zone = number | undefined;
  export type created_at = number;
  export type updated_at = number;
  export type time = number;
  export type deleted_at = number | undefined;
  export type registered_manual = boolean;
  export type user_race_id = number | undefined;
  export type uploaded_way_points = boolean;
  export type removed_way_point_records = boolean;
  export type activity_source = ActivitySource;
  export type heart_rate = number | undefined;
  export type single_value_key = 0;
  export type user_id_activity_type = string; // railsに無い
  export type gross_time = number;
  export type highest_alt = number;
  export type lowest_alt = number;
  export type steps = number;
  export type lap_info_net = LapInfo[];
  export type lap_info_gross = LapInfo[];
  export type is_cheater = boolean | undefined;
  export type cheat_type = CheatType | undefined;
  export type motion_rate = MotionRate | undefined;
}

export interface LapInfo {
  unit: string, //単位 (km/mile)
  section: number, //区間
  timestamp: number, //ラップ開始時刻(sec)
  lapTime: number, // ラップタイム(sec)
  ascent: number, //獲得標高（上り）(m)
  descent: number, //獲得標高（下り）(m)
  steps?: number, //歩数
  averageHeartRate?: number // 平均心拍数
  location?: Geo; // 緯度経度
}

export interface Geo {
  latitude: number;
  longitude: number;
}

export interface MotionRate {
  running: number;
  walking: number;
  cycling: number;
  automotive: number;
  unknown: number;
}

export interface Activity {
  id: ActivityField.id;
  user_id?: ActivityField.user_id;
  uuid?: ActivityField.uuid;
  activity_type?: ActivityField.activity_type;
  pace: ActivityField.pace;
  distance: ActivityField.distance;
  calorie: ActivityField.calorie;
  climb_up: ActivityField.climb_up;
  climb_down: ActivityField.climb_down;
  beginning_at?: ActivityField.beginning_at;
  end_of_at?: ActivityField.end_of_at;
  time_zone?: ActivityField.time_zone;
  created_at: ActivityField.created_at;
  updated_at: ActivityField.updated_at;
  time: ActivityField.time;
  deleted_at?: ActivityField.deleted_at;
  registered_manual: ActivityField.registered_manual;
  user_race_id?: ActivityField.user_race_id;
  uploaded_way_points: ActivityField.uploaded_way_points;
  removed_way_point_records: ActivityField.removed_way_point_records;
  activity_source: ActivityField.activity_source;
  heart_rate?: ActivityField.heart_rate;
  single_value_key: ActivityField.single_value_key;
  user_id_activity_type: ActivityField.user_id_activity_type;
  // 以降はv2新規追加パラメータ
  gross_time?: ActivityField.gross_time;
  highest_alt?: ActivityField.highest_alt;
  lowest_alt?: ActivityField.lowest_alt;
  steps?: ActivityField.steps;
  lap_info_net?: ActivityField.lap_info_net;
  lap_info_gross?: ActivityField.lap_info_gross;
  is_cheater?: ActivityField.is_cheater;
  cheat_type?: ActivityField.cheat_type;
  motion_rate?: ActivityField.motion_rate;
  data_origin?: string; // ActivitySource#HealthKit/GoogleFitで利用。2026/2以降に導入
}
